export function orderDepartmentsTree(departments = []) {
  if (!Array.isArray(departments) || departments.length === 0) return [];

  const byParent = new Map();
  const roots = [];

  departments.forEach((dept) => {
    if (!dept) return;
    const parentId =
      Array.isArray(dept.path) && dept.path.length > 0 ? dept.path[dept.path.length - 1]?.id : null;
    if (parentId == null) {
      roots.push(dept);
      return;
    }
    if (!byParent.has(parentId)) byParent.set(parentId, []);
    byParent.get(parentId).push(dept);
  });

  const result = [];
  const walk = (nodes) => {
    [...nodes]
      .sort((a, b) => {
        // Sort by sequence if both departments have it
        if (a?.sequence != null && b?.sequence != null) {
          return a.sequence - b.sequence;
        }
        // Departments with sequence come before those without
        if (a?.sequence != null) return -1;
        if (b?.sequence != null) return 1;
        // Fall back to alphabetical by name
        return String(a?.name || "").localeCompare(String(b?.name || ""));
      })
      .forEach((node) => {
        result.push(node);
        if (byParent.has(node.id)) {
          walk(byParent.get(node.id));
        }
      });
  };

  walk(roots);
  return result;
}

export function getHierarchyLabel(dept) {
  const ancestors = Array.isArray(dept?.path) ? dept.path.map((p) => p.name) : [];
  return [...ancestors, dept?.name || ""].filter(Boolean).join(" ");
}

export function getDepartmentDepth(dept) {
  return Array.isArray(dept?.path) ? dept.path.length : 0;
}

export function getPathSegments(dept) {
  const ancestors = Array.isArray(dept?.path) ? dept.path.map((p) => p.name) : [];
  return [...ancestors, dept?.name || ""].filter(Boolean);
}

function normalizeDepartmentCode(dept) {
  const raw = dept?.code_str ?? dept?.code ?? "";
  const str = String(raw);
  const match = str.match(/\d+/);
  return match ? match[0] : str.replace(/^#/, "");
}

function getParentDepartmentId(dept) {
  return dept?.parent_department_id ?? dept?.parent_id ?? dept?.parent ?? null;
}

/**
 * Maps a flat department API list to DepartmentPickerField items with ancestor paths.
 */
export function mapApiDepartmentsToPickerList(flatList = []) {
  if (!Array.isArray(flatList) || flatList.length === 0) return [];

  const byId = new Map();
  flatList.forEach((dept) => {
    if (!dept?.id) return;
    byId.set(dept.id, {
      id: dept.id,
      parentId: getParentDepartmentId(dept),
    });
  });

  const buildPath = (id) => {
    const path = [];
    let parentId = byId.get(id)?.parentId ?? null;
    const visited = new Set();
    while (parentId != null && byId.has(parentId) && !visited.has(parentId)) {
      visited.add(parentId);
      const parentDept = flatList.find((d) => d.id === parentId);
      if (!parentDept) break;
      path.unshift({ id: parentDept.id, name: parentDept.name });
      parentId = getParentDepartmentId(parentDept);
    }
    return path;
  };

  return flatList
    .filter((dept) => dept?.id)
    .map((dept) => ({
      id: dept.id,
      name: dept.name,
      code: normalizeDepartmentCode(dept),
      path: buildPath(dept.id),
    }));
}
